import { Link, useSearchParams } from 'react-router-dom'
import { buildRegistrationStepPath, resolveAuthRedirect, useAuth } from '@/features/auth'
import { routes } from '@/shared/config'
import { RETURN_TO_QUERY_PARAM, resolveSafeInternalReturnTo } from '@/shared/lib'

type LegalPageProps = {
  title: string
  sections: Array<{ heading: string; body: string[] }>
}

export function LegalPage({ title, sections }: LegalPageProps) {
  const [searchParams] = useSearchParams()
  const { user, isAuthenticated } = useAuth()
  const explicitReturnTo = resolveSafeInternalReturnTo(searchParams.get(RETURN_TO_QUERY_PARAM))
  const userNeedsOnboarding =
    !!user &&
    (user.profileComplete === false ||
      (user.missingProfileFields?.length ?? 0) > 0 ||
      user.acceptedUserAgreement === false)
  const fallbackReturnTo = userNeedsOnboarding
    ? buildRegistrationStepPath('profile')
    : isAuthenticated && user
      ? resolveAuthRedirect({
          subscriptionActive: user.subscriptionActive,
          subscriptionStatus: user.subscriptionStatus,
        })
      : routes.registration
  const returnTo = explicitReturnTo ?? fallbackReturnTo
  const returnLabel =
    explicitReturnTo || userNeedsOnboarding
      ? 'Вернуться к форме'
      : isAuthenticated && user
        ? 'Вернуться в сервис'
        : 'Перейти ко входу'

  return (
    <main className="min-h-screen w-full bg-[#080808] px-4 py-8 text-[#FEFEFE] md:px-8 md:py-10">
      <div className="mx-auto w-full max-w-6xl">
        <div className="mb-8">
          <Link to={returnTo} className="text-sm underline">
            {returnLabel}
          </Link>
        </div>
        <h1 className="mb-8 text-2xl font-semibold md:text-3xl">{title}</h1>
        <div className="space-y-8">
          {sections.map((section) => (
            <section key={section.heading} className="space-y-3">
              <h2 className="text-lg font-semibold">{section.heading}</h2>
              <div className="space-y-3">
                {section.body.map((paragraph, index) => (
                  <p key={`${section.heading}-${index}`} className="leading-relaxed text-[#E8E8E8]">
                    {paragraph}
                  </p>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </main>
  )
}
